/**
 * shared/rubrics/readinessPreseed.js — rubric chấm mức độ sẵn sàng gọi vốn cho
 * vòng angel và pre-seed.
 *
 * Đây là DỮ LIỆU, không phải code. Bản này là seed để đổ vào Firestore
 * collection `rubrics` (đặc tả mục 7). Sau đó sửa trọng số hay thêm tiêu chí là
 * sửa document, không phải build lại app.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * HAI QUY TẮC BẮT BUỘC VỚI MỌI TIÊU CHÍ
 *
 * 1. Phải có `whyItMatters` và `howToFix`. Sản phẩm này dạy founder lần đầu;
 *    một tiêu chí không giải thích được vì sao nó quan trọng thì không nên tồn
 *    tại. Điểm số không có lời giải thích chỉ làm người ta lo, không giúp họ
 *    làm gì tiếp.
 *
 * 2. Rubric đã publish là BẤT BIẾN. Sửa = tăng `version`, tạo bản mới. Snapshot
 *    cũ trỏ về version cũ và vĩnh viễn không đổi điểm.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Năm trục được chọn theo thứ tự nhà đầu tư giai đoạn đầu thực sự quan tâm:
 * người trước, rồi vấn đề, rồi bằng chứng có người cần, sau cùng mới tới thị
 * trường và hồ sơ. Ở pre-seed, đội ngũ nặng hơn mọi thứ khác cộng lại.
 *
 * Version 2 (10/2026): thêm tiêu chí kênh bán hàng (SALES_CHANNEL) vào trục
 * thị trường, theo bảng chấm điểm của Bill Payne (Kauffman Foundation). Trọng
 * số các trục giữ nguyên. Công cụ định giá pre-seed đọc tiêu chí này cho trục
 * "Kênh bán hàng" của mình, xem trucTuSanSang trong valuationPreseed.js.
 *
 * Version 3 (11/2026): thêm hai tiêu chí trong khung 5T nhà đầu tư dùng
 * (Team, TAM, Technology, Traction, Trenches) mà founder hay bỏ sót nhất:
 * đòn bẩy mở rộng (SCALE_LEVERAGE) và hào phòng thủ (MOAT), đặt trong trục
 * vấn đề và giải pháp. Trọng số các trục giữ nguyên. Xem bài Playbook
 * toan-hoc-quy-vc-vi-sao-quy-tu-choi-startup-co-lai.
 */

/**
 * Năm mức của kênh bán hàng, từ bảng Payne. Lưu dạng số (0 tới 4) trong
 * profile.gtm.channelLevel để chấm bằng thang bậc.
 */
export const MUC_KENH_BAN = Object.freeze([
  { v: 0, label: 'Chưa nghĩ tới kênh bán' },
  { v: 1, label: 'Đã liệt kê nhiều kênh có thể dùng' },
  { v: 2, label: 'Đã chọn một hai kênh chính' },
  { v: 3, label: 'Đã thử và có khách đến từ kênh đó' },
  { v: 4, label: 'Kênh đã mang khách về đều đặn' },
]);

/** Bốn mức của đòn bẩy mở rộng (Technology trong 5T). Lưu ở profile.business.scaleLevel. */
export const MUC_DON_BAY = Object.freeze([
  { v: 0, label: 'Chi phí tăng gần như theo số khách' },
  { v: 1, label: 'Đã biết phần việc nào tự động hoá được' },
  { v: 2, label: 'Đã tự động hoá phần lớn việc phục vụ khách' },
  { v: 3, label: 'Có số liệu chi phí mỗi khách giảm khi quy mô tăng' },
]);

/** Bốn mức của hào phòng thủ (Trenches trong 5T). Lưu ở profile.business.moatLevel. */
export const MUC_HAO_PHONG_THU = Object.freeze([
  { v: 0, label: 'Chưa có, đối thủ làm lại được ngay' },
  { v: 1, label: 'Mới là lợi thế đi trước' },
  { v: 2, label: 'Đang xây một lợi thế khó sao chép' },
  { v: 3, label: 'Có bằng chứng lợi thế đó đang giữ khách' },
]);

export const READINESS_PRESEED = {
  rubricId: 'readiness-preseed',
  version: 3,
  status: 'PUBLISHED',
  label: 'Sẵn sàng gọi vốn vòng angel và pre-seed',
  appliesTo: { stages: ['IDEA', 'PROTOTYPE', 'ANGEL', 'PRESEED'] },

  axes: [
    {
      key: 'TEAM',
      label: 'Đội ngũ',
      weight: 30,
      criteria: [
        {
          key: 'FULLTIME_FOUNDER',
          label: 'Có ít nhất một người sáng lập làm toàn thời gian',
          points: 10,
          rule: { path: 'profile.team.founders', op: 'someTrue', field: 'fullTime' },
          whyItMatters:
            'Ở vòng đầu, nhà đầu tư đặt cược vào người chứ không vào sản phẩm. ' +
            'Không ai toàn thời gian nghĩa là chưa ai thực sự đánh cược.',
          howToFix:
            'Trước khi gặp nhà đầu tư, ít nhất một người phải nghỉ việc cũ hoặc có ' +
            'lộ trình rõ ràng và có thời hạn để chuyển sang toàn thời gian.',
        },
        {
          key: 'DOMAIN_EXPERTISE',
          label: 'Có người am hiểu ngành mình đang làm',
          points: 8,
          rule: {
            path: 'profile.team.founders', op: 'someMatch',
            field: 'domainYears', subOp: 'gte', value: 3, count: 1,
          },
          whyItMatters:
            'Câu hỏi "vì sao là các bạn làm việc này" gần như luôn được hỏi. Kinh ' +
            'nghiệm trong ngành là câu trả lời dễ kiểm chứng nhất.',
          howToFix:
            'Nếu cả nhóm đều mới với ngành, hãy bổ sung cố vấn có uy tín trong ngành ' +
            'và nêu rõ vai trò của họ.',
        },
        {
          key: 'VESTING_SIGNED',
          label: 'Tất cả người sáng lập đã ký thoả thuận vesting',
          points: 8,
          rule: { path: 'profile.team.founders', op: 'everyTrue', field: 'vesting.hasVesting' },
          whyItMatters:
            'Không có vesting, một người rời đi sau ba tháng vẫn giữ nguyên cổ phần. ' +
            'Nhiều quỹ dừng thẩm định ngay khi phát hiện điều này.',
          howToFix:
            'Ký vesting 4 năm, cliff 12 tháng cho toàn bộ người sáng lập. Nên làm ' +
            'trước khi bắt đầu nói chuyện với nhà đầu tư.',
        },
        {
          key: 'TEAM_SIZE',
          label: 'Có từ hai người sáng lập trở lên',
          points: 4,
          rule: { path: 'profile.team.founders', op: 'countGte', value: 2 },
          whyItMatters:
            'Không phải điều kiện bắt buộc, nhưng một mình gánh mọi việc là rủi ro ' +
            'mà nhà đầu tư luôn cân nhắc.',
          howToFix:
            'Nếu đang làm một mình, hãy chuẩn bị sẵn câu trả lời về cách bạn bù đắp ' +
            'các mảng còn thiếu.',
        },
      ],
    },

    {
      key: 'PROBLEM',
      label: 'Vấn đề và giải pháp',
      weight: 20,
      criteria: [
        {
          key: 'ONE_LINER',
          label: 'Nói được doanh nghiệp làm gì trong một câu',
          points: 6,
          rule: { path: 'profile.business.oneLiner', op: 'lengthGte', value: 20 },
          whyItMatters:
            'Nếu không tóm tắt được trong một câu, nhà đầu tư sẽ không nhớ được bạn ' +
            'sau khi rời phòng họp.',
          howToFix:
            'Viết một câu dưới 140 ký tự: làm gì, cho ai, khác gì. Bỏ hết tính từ.',
        },
        {
          key: 'PROBLEM_STATED',
          label: 'Mô tả rõ vấn đề đang giải quyết',
          points: 7,
          rule: { path: 'profile.business.problem', op: 'lengthGte', value: 50 },
          whyItMatters:
            'Nhà đầu tư giai đoạn đầu mua vấn đề trước, giải pháp sau. Vấn đề mơ hồ ' +
            'là dấu hiệu chưa nói chuyện đủ với khách hàng.',
          howToFix:
            'Mô tả vấn đề bằng ngôn ngữ của khách hàng, kèm ai đang chịu và họ đang ' +
            'xoay xở bằng cách nào.',
        },
        {
          key: 'PRODUCT_EXISTS',
          label: 'Đã có ít nhất nguyên mẫu chạy được',
          points: 7,
          rule: {
            path: 'profile.business.productStage', op: 'in',
            value: ['PROTOTYPE', 'MVP', 'LIVE', 'SCALING'],
          },
          whyItMatters:
            'Gọi vốn khi mới có ý tưởng là được, nhưng khó hơn nhiều. Một thứ chạy ' +
            'được cho thấy nhóm có khả năng thực thi.',
          howToFix:
            'Làm một bản chạy được dù thô, dù chỉ giải quyết một phần vấn đề.',
        },
        {
          key: 'SCALE_LEVERAGE',
          label: 'Lớn nhanh được mà chi phí không phình theo',
          points: 5,
          scalePath: 'profile.business.scaleLevel',
          scale: [
            { gte: 3, points: 5 },
            { gte: 2, points: 4 },
            { gte: 1, points: 2 },
          ],
          whyItMatters:
            'Quỹ chỉ đầu tư khi công ty có thể lớn gấp nhiều lần, đủ để trả lại cả ' +
            'quỹ. Nếu mỗi khách mới đòi thêm người và thêm chi phí gần bằng doanh thu ' +
            'họ mang lại, công ty lớn lên mà không giàu lên.',
          howToFix:
            'Tính chi phí phục vụ một khách hôm nay và khi gấp 10 lần quy mô. Chỉ ra ' +
            'phần việc nào đã hoặc sẽ tự động hoá, và đưa con số đó vào deck.',
        },
        {
          key: 'MOAT',
          label: 'Có lợi thế đối thủ khó sao chép',
          points: 5,
          scalePath: 'profile.business.moatLevel',
          scale: [
            { gte: 3, points: 5 },
            { gte: 2, points: 4 },
            { gte: 1, points: 2 },
          ],
          whyItMatters:
            'Nhà đầu tư luôn hỏi: nếu bạn thành công, điều gì ngăn một công ty lớn ' +
            'làm y hệt. Đi trước vài tháng không đủ, họ cần thấy lý do khách ở lại ' +
            'và đối thủ khó chen vào.',
          howToFix:
            'Chọn một lợi thế đang thật sự xây: dữ liệu riêng, hiệu ứng mạng, chi phí ' +
            'chuyển đổi của khách, giấy phép, hoặc kênh phân phối độc quyền. Nói nó ' +
            'trong một câu, kèm bằng chứng như tỷ lệ khách ở lại.',
        },
      ],
    },

    {
      key: 'EVIDENCE',
      label: 'Bằng chứng có người cần',
      weight: 20,
      criteria: [
        {
          key: 'EVIDENCE_COUNT',
          label: 'Có bằng chứng nhu cầu từ bên ngoài',
          points: 12,
          scalePath: 'profile.traction.evidence.length',
          scale: [
            { gte: 5, points: 12 },
            { gte: 3, points: 9 },
            { gte: 1, points: 5 },
          ],
          whyItMatters:
            'Chưa có doanh thu không sao, nhưng phải có gì đó chứng minh người ngoài ' +
            'thực sự muốn thứ này: thư cam kết, triển khai thử, đơn đặt trước, hợp đồng.',
          howToFix:
            'Xin thư quan tâm hoặc thoả thuận triển khai thử từ ba khách hàng tiềm ' +
            'năng. Một pilot có thu phí đáng giá hơn mười lời khen.',
        },
        {
          key: 'PAID_EVIDENCE',
          label: 'Có ít nhất một bằng chứng đã trả tiền hoặc đã ký',
          points: 8,
          rule: {
            path: 'profile.traction.evidence', op: 'someMatch',
            field: 'type', subOp: 'in', value: ['PAID_PILOT', 'CONTRACT', 'PREORDER'], count: 1,
          },
          whyItMatters:
            'Có người chịu trả tiền là ranh giới giữa "họ nói thích" và "họ thực sự cần".',
          howToFix:
            'Chuyển một pilot miễn phí thành có thu phí, dù số tiền nhỏ. Con số không ' +
            'quan trọng bằng việc có người bấm nút trả tiền.',
        },
      ],
    },

    {
      key: 'MARKET',
      label: 'Thị trường và kênh bán',
      weight: 10,
      criteria: [
        {
          key: 'SOM_DEFINED',
          label: 'Đã ước lượng thị trường có thể chiếm được',
          points: 5,
          rule: { path: 'profile.market.som', op: 'exists' },
          whyItMatters:
            'Không phải để có con số đẹp, mà để cho thấy bạn biết mình đang nhắm vào ai.',
          howToFix:
            'Dùng công cụ quy mô thị trường, dựng từ dưới lên: bao nhiêu khách hàng ' +
            'khả thi × giá trị mỗi khách.',
        },
        {
          key: 'BOTTOM_UP',
          label: 'Ước lượng theo hướng bottom-up, không lấy số báo cáo ngành',
          points: 5,
          rule: { path: 'profile.market.method', op: 'equals', value: 'BOTTOM_UP' },
          whyItMatters:
            'Con số "thị trường 10 tỷ đô" lấy từ báo cáo ngành là dấu hiệu chưa suy ' +
            'nghĩ kỹ. Nhà đầu tư giai đoạn đầu muốn thấy giả định của chính bạn.',
          howToFix:
            'Bắt đầu từ số khách hàng bạn thực sự tiếp cận được và số tiền mỗi khách ' +
            'trả, rồi nhân lên. Ghi rõ từng giả định.',
        },
        {
          key: 'SALES_CHANNEL',
          label: 'Đã thử và xác định được kênh bán hàng chính',
          points: 6,
          scalePath: 'profile.gtm.channelLevel',
          scale: [
            { gte: 4, points: 6 },
            { gte: 3, points: 5 },
            { gte: 2, points: 3 },
            { gte: 1, points: 1 },
          ],
          whyItMatters:
            'Thị trường lớn chưa đủ, nhà đầu tư muốn biết khách sẽ đến với bạn bằng ' +
            'đường nào và tốn bao nhiêu. Kênh đã thử và có khách là bằng chứng rằng ' +
            'tiền gọi vốn sẽ biến thành tăng trưởng.',
          howToFix:
            'Chọn một hai kênh hợp với khách hàng mục tiêu, chạy thử với ngân sách nhỏ ' +
            'và ghi lại chi phí cho mỗi khách. Một kênh đã có số liệu thuyết phục hơn ' +
            'danh sách mười kênh "sẽ làm".',
        },
      ],
    },

    {
      key: 'READINESS',
      label: 'Hồ sơ và tài chính',
      weight: 20,
      criteria: [
        {
          key: 'RUNWAY_OK',
          label: 'Còn đủ runway để đàm phán ở thế chủ động',
          points: 6,
          scalePath: 'derived.runwayMonths',
          scale: [
            { gte: 9, points: 6 },
            { gte: 6, points: 4 },
            { gte: 3, points: 2 },
          ],
          whyItMatters:
            'Một vòng pre-seed ở Việt Nam thường mất 3–6 tháng để đóng. Còn dưới 6 ' +
            'tháng là bạn phải nhận điều khoản bên kia đưa ra.',
          howToFix:
            'Bắt đầu gọi vốn khi còn ít nhất 9 tháng. Nếu đã dưới mức đó, cắt chi phí ' +
            'trước khi bước vào đàm phán.',
        },
        {
          key: 'RAISE_AMOUNT',
          label: 'Biết rõ mình gọi bao nhiêu',
          points: 4,
          rule: { path: 'profile.fundraise.target', op: 'exists' },
          whyItMatters:
            'Câu "các bạn cần bao nhiêu" luôn được hỏi trong mười phút đầu. Không có ' +
            'câu trả lời là mất điểm ngay.',
          howToFix:
            'Tính số tiền đủ nuôi 18 tháng tới cột mốc tiếp theo. Con số phải suy ra ' +
            'được từ kế hoạch, không phải bốc.',
        },
        {
          key: 'USE_OF_FUNDS',
          label: 'Có phân bổ cụ thể số tiền định gọi',
          points: 4,
          rule: { path: 'profile.fundraise.useOfFunds', op: 'countGte', value: 2 },
          whyItMatters:
            'Nhà đầu tư muốn biết tiền đi đâu và mua được cột mốc gì, không phải để ' +
            'duy trì hoạt động chung chung.',
          howToFix:
            'Chia theo hạng mục: sản phẩm, tuyển người, bán hàng, dự phòng. Mỗi hạng ' +
            'mục gắn với một kết quả cụ thể.',
        },
        {
          key: 'INCORPORATED',
          label: 'Đã thành lập pháp nhân',
          points: 3,
          rule: {
            path: 'profile.identity.entityType', op: 'in',
            value: ['LLC1', 'LLC2', 'JSC', 'FOREIGN'],
          },
          whyItMatters:
            'Không có pháp nhân thì không có gì để nhận vốn vào. Với vòng angel có ' +
            'thể thu xếp sau, nhưng sẽ làm chậm việc đóng vòng.',
          howToFix:
            'Với startup định gọi vốn, công ty cổ phần là loại hình linh hoạt nhất cho ' +
            'việc phát hành cổ phần và lập ESOP.',
        },
        {
          key: 'IP_ASSIGNED',
          label: 'Sở hữu trí tuệ đã chuyển về công ty',
          points: 3,
          rule: {
            path: 'profile.legal.checklist.ipAssignment', op: 'in', value: ['DONE', 'NA'],
          },
          whyItMatters:
            'Nếu mã nguồn hay thương hiệu vẫn đứng tên cá nhân, công ty không thực sự ' +
            'sở hữu thứ nhà đầu tư đang mua. Đây là điểm thẩm định pháp lý luôn soi.',
          howToFix:
            'Ký thoả thuận chuyển giao sở hữu trí tuệ từ từng người sáng lập và nhân ' +
            'sự sang công ty.',
        },
      ],
    },
  ],

  flags: [
    {
      code: 'KHONG_AI_TOAN_THOI_GIAN',
      severity: 'HIGH',
      rule: { path: 'profile.team.founders', op: 'everyFalse', field: 'fullTime' },
      message: 'Không có người sáng lập nào làm toàn thời gian.',
      fix: 'Đây thường là lý do bị từ chối ngay vòng sàng lọc, trước cả khi xem sản phẩm.',
    },
    {
      code: 'CHUA_KY_VESTING',
      severity: 'HIGH',
      rule: { path: 'profile.team.founders', op: 'everyFalse', field: 'vesting.hasVesting' },
      message: 'Chưa người sáng lập nào ký vesting.',
      fix: 'Xử lý trước khi gặp nhà đầu tư. Đây là việc làm trong một tuần nhưng ảnh hưởng tới cả vòng gọi vốn.',
    },
    {
      code: 'KHONG_CO_BANG_CHUNG',
      severity: 'MEDIUM',
      rule: { path: 'profile.traction.evidence', op: 'countGte', value: 1 },
      dao: true,
      message: 'Chưa có bằng chứng nào từ bên ngoài về nhu cầu.',
      fix: 'Ba thư quan tâm từ khách hàng thật có sức nặng hơn mười slide phân tích thị trường.',
    },
    {
      code: 'RUNWAY_QUA_NGAN',
      severity: 'HIGH',
      rule: { path: 'derived.runwayMonths', op: 'lt', value: 6 },
      message: 'Runway còn dưới 6 tháng khi bắt đầu gọi vốn.',
      fix: 'Cắt chi phí để kéo dài thời gian, hoặc tìm vốn cầu nối song song. Đàm phán khi sắp hết tiền là đàm phán ở thế yếu.',
    },
  ],

  bands: [
    { min: 80, key: 'SAN_SANG', label: 'Sẵn sàng gặp nhà đầu tư' },
    { min: 60, key: 'GAN_SAN_SANG', label: 'Gần sẵn sàng, còn vài việc' },
    { min: 40, key: 'DANG_CHUAN_BI', label: 'Đang chuẩn bị' },
    { min: 0, key: 'CHUA_SAN_SANG', label: 'Còn sớm để gọi vốn' },
  ],
};

export default READINESS_PRESEED;
